import { useEffect, useState, useCallback } from 'react';
import { Card, Typography, Table, Button, Tag, Space, message, Popconfirm } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;

/** Flattens Keycloak's device->sessions shape into one row per session,
 * defensively — field names/nesting aren't verified against a live realm. */
function flattenDevices(devices) {
    if (!Array.isArray(devices)) return [];
    return devices.flatMap(device => {
        const sessions = Array.isArray(device.sessions) && device.sessions.length > 0 ? device.sessions : [device];
        return sessions.map(session => ({
            id: session.id || device.id,
            browser: device.browser || session.browser,
            os: [device.os, device.osVersion].filter(Boolean).join(' '),
            ipAddress: session.ipAddress || device.ipAddress,
            lastAccess: session.lastAccess || device.lastAccess,
            current: session.current ?? device.current ?? false,
        }));
    });
}

/** Lists active Keycloak sessions ("signed-in devices") and lets the user
 * sign individual ones out, or everywhere at once. */
function ActiveSessions() {
    const api = useApi();
    const { logout } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [signingOutAll, setSigningOutAll] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        return api.get('/api/account/security/sessions')
            .then(data => setSessions(flattenDevices(data)))
            .catch(() => message.error('Could not load active sessions'))
            .finally(() => setLoading(false));
    }, [api]);

    useEffect(() => { load(); }, [load]);

    const handleSignOut = id => {
        setBusyId(id);
        api.del(`/api/account/security/sessions/${id}`)
            .then(() => { message.success('Session signed out'); return load(); })
            .catch(err => message.error(err.message || 'Could not sign out session'))
            .finally(() => setBusyId(null));
    };

    const handleSignOutAll = () => {
        setSigningOutAll(true);
        api.del('/api/account/security/sessions')
            .then(() => logout())
            .catch(err => {
                message.error(err.message || 'Could not sign out all sessions');
                setSigningOutAll(false);
            });
    };

    const columns = [
        {
            title: 'Device',
            key: 'device',
            render: (_, row) => (
                <div>
                    <div><Text strong>{row.browser || 'Unknown browser'}</Text> {row.current && <Tag color="blue">This device</Tag>}</div>
                    {row.os && <Text type="secondary" style={{ fontSize: 12 }}>{row.os}</Text>}
                </div>
            ),
        },
        { title: 'IP address', dataIndex: 'ipAddress', key: 'ipAddress' },
        {
            title: 'Last active',
            dataIndex: 'lastAccess',
            key: 'lastAccess',
            render: ts => ts ? new Date(ts).toLocaleString() : '—',
        },
        {
            title: '',
            key: 'actions',
            render: (_, row) => (
                <Button
                    size="small"
                    danger
                    icon={<LogoutOutlined />}
                    loading={busyId === row.id}
                    onClick={() => handleSignOut(row.id)}
                >
                    Sign out
                </Button>
            ),
        },
    ];

    return (
        <Card style={{ marginTop: 16 }}>
            <Space align="center" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
                <Title level={5} style={{ margin: 0 }}>Signed-in devices</Title>
                <Popconfirm
                    title="Sign out everywhere?"
                    description="This signs you out of every device, including this one."
                    onConfirm={handleSignOutAll}
                    okText="Sign out everywhere"
                    okButtonProps={{ danger: true }}
                >
                    <Button danger loading={signingOutAll}>Sign out everywhere</Button>
                </Popconfirm>
            </Space>
            <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={sessions}
                pagination={false}
                size="small"
            />
        </Card>
    );
}

export default ActiveSessions;
